import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { scenarioCategories, scenarioTypes } from "@/mocks/home";

export default function ScenarioFilter() {
  const [category, setCategory] = useState(scenarioCategories[0]);
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return scenarioTypes.filter((item) => {
      const matchCategory =
        category === scenarioCategories[0] || item.category === category;
      const matchKeyword =
        kw.length === 0 ||
        item.title.toLowerCase().includes(kw) ||
        item.place.toLowerCase().includes(kw) ||
        item.tone.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw);
      return matchCategory && matchKeyword;
    });
  }, [category, keyword]);

  const reset = () => {
    setCategory(scenarioCategories[0]);
    setKeyword("");
  };

  return (
    <section id="scenarios" className="section-anchor bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="SCENE INDEX"
          title="十二个情境条目，按空间自由筛选"
          desc="每条情境都标注了发生场所与氛围基调。选择空间分类或输入关键词，即可在本页已有内容中快速缩小范围。"
        />

        <div className="mt-8 md:mt-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap gap-1.5 p-1.5 rounded-full bg-background-50 border border-background-200 w-fit max-w-full">
            {scenarioCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={`px-3.5 py-1.5 rounded-full font-label text-sm whitespace-nowrap transition-colors ${
                  category === item
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-700 hover:bg-secondary-100"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索场所、氛围或关键词"
              aria-label="搜索情境条目"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3 font-label text-sm text-foreground-600">
          <span>
            共 <strong className="text-primary-700">{filtered.length}</strong> 条情境
          </span>
          {filtered.length !== scenarioTypes.length && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-foreground-500 hover:text-primary-700 transition-colors"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item) => (
              <article
                key={item.title}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-accent-300"
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-900 font-label text-xs whitespace-nowrap">
                    {item.category}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-7 text-foreground-700 flex-1">
                  {item.summary}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-background-200 pt-3.5 font-label text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-map-pin-2-line text-primary-600" />
                    {item.place}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-temp-cold-line text-secondary-600" />
                    {item.tone}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 flex flex-col items-center justify-center text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-400" />
            <p className="mt-3 font-label text-sm text-foreground-600">
              没有匹配的情境条目，换个关键词试试
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 font-label text-sm hover:bg-primary-600 transition-colors"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}